import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '@environments/environment';

import { catchError, debounceTime, tap } from 'rxjs/operators';
import { forkJoin, throwError } from 'rxjs';

import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { FormPatientNephroService } from '@services/form/form-patient-nephro.service';
import { AuthService } from '@services/security';
import { CompanyService } from '@app/company/services/company.service';
import { FormatFormService } from './format-form.service';
import { ErrorFormService } from './error-form.service';

@Injectable({ providedIn: 'root' })
export class InclusionNephroService {
    patient;
    comment = '';
    typeChanged = new EventEmitter<void>();

    constructor(
        private http: HttpClient,
        private formatService: FormatFormService,
        private formPatientNephroService: FormPatientNephroService,
        private companyService: CompanyService,
        private errorService: ErrorFormService,
        private router: Router,
        private authService: AuthService,
        private toastr: ToastrService,
        private translate: TranslateService,
    ) {}

    initInclusion() {
        this.errorService.resetErrors();
        this.comment = '';
        const currentUser = this.authService.getCurrentUser();
        this.patient = {
            absenceContact: {},
            phones: {},
            signedConsent: true,
            medicalData: {
                hypokalemia: 3.5,
                hyperkalemia: 5.5,
                nephroType: 'stage4',
                mainDiagnosis: {},
                significantEvent: {},
                creatinineUnit: currentUser.biologicDataUnit.creatinine,
                proteinuria24hUnit: currentUser.biologicDataUnit.proteinuria24h,
                proteinuriaSampleUnit:
                    currentUser.biologicDataUnit.proteinuriaSample,
                racUnit: currentUser.biologicDataUnit.rac,
            },
            inclusion: {
                type: 'commonLaw',
            },
            treatment: {},
            address: {},
            spokenLanguage: ['fr'],
            prescriber: {},
            toComplete: false,
            identityStatus: 'provisional',
            mobileApp: true,
        };
        return this.initUsers();
    }

    initUsers() {
        const promises = [];
        if (this.authService.isAdmin) {
            promises.push(this.formPatientNephroService.initCompanies());
            promises.push(
                this.formPatientNephroService.initSpecialistsFromPatient(
                    this.patient,
                ),
            );
        }
        if (this.authService.isUser) {
            promises.push(this.initCurrentCompany());
            promises.push(
                this.formPatientNephroService.initSpecialistsFromCompanyId(
                    this.patient,
                    this.authService?.getCurrentCompany(),
                ),
            );
        }
        if (this.authService.canEditMedicalInfos) {
            promises.push(
                this.formPatientNephroService.loadImmunosuppressants(),
            );
        }
        return forkJoin(promises);
    }

    initCurrentCompany() {
        return this.companyService
            .getCompany(this.authService?.getCurrentCompany())
            .pipe(
                tap((company) => {
                    this.patient.companyId = company;
                    this.patient.inclusion.type =
                        company?.inclusionTypes?.[0] || 'commonLaw';
                    this.patient.inclusion.enableTensio =
                        company?.services?.nephro?.enableTensio;
                    if (this.authService.canBeReferent.all) {
                        this.patient.specialistId =
                            this.authService.getCurrentUser();
                    }
                }),
            );
    }

    includedPatient() {
        const formData = this.formatService.formatPatientNephro(this.patient);
        formData.append('comment', this.comment);
        return this.http
            .post<any>(`${environment.apiUrl}/patient`, formData, {
                params: {
                    disease: 'nephro',
                },
            })
            .pipe(
                debounceTime(200),
                tap((result) => {
                    this.toastr.success(
                        this.translate.instant('inclusion.fastsuccess', {
                            patientId: result?.patient?._id,
                            userName: `${result?.patient?.firstName} ${result?.patient?.lastName}`,
                        }),
                        null,
                        {
                            enableHtml: true,
                            messageClass: 'toastLink',
                            closeButton: true,
                        },
                    );
                    this.router.navigate(['patient', result?.patient?._id]);
                }),
                catchError((err) => {
                    this.errorService.initErrors(err?.error?.errors);
                    return throwError(() => err);
                }),
            );
    }
}
